import { DriveFile, DrivePermission, SheetData } from '../types/google-api';
import { youtubeService } from './youtubeService';

// ---> START: Define Google Drive API types
interface DriveCopyResponse {
  kind: 'drive#file';
  id: string;
  name: string;
  mimeType: string;
}

interface DriveRenameRequest {
  name: string;
}
// ---> END: Define Google Drive API types

export class GoogleSheetsService {
  private initialized = false;
  private tokenClient: any;

  // Add rate limiting helper
  private requestQueue: { [key: string]: Promise<any> } = {};
  private requestTimestamps: number[] = [];
  private readonly MAX_REQUESTS_PER_MINUTE = 30; // Adjust based on your quota
  private readonly REQUEST_WINDOW_MS = 60000; // 1 minute in milliseconds
  private readonly MIN_REQUEST_DELAY_MS = 500; // Minimum delay between requests

  async init(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      // Wait for gapi to be available
      let retries = 0;
      while (!window.gapi && retries < 10) {
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }

      if (!window.gapi) {
        throw new Error('Failed to load Google API client library');
      }

      // Load the client
      await new Promise<void>((resolve) => {
        window.gapi.load('client', resolve);
      });

      // Load the auth2 library
      await new Promise<void>((resolve) => {
        window.gapi.load('auth2', resolve);
      });

      // Initialize the client
      const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

      if (!apiKey || !clientId) {
        throw new Error('Missing API key or client ID');
      }

      await window.gapi.client.init({
        apiKey,
        discoveryDocs: [
          'https://sheets.googleapis.com/$discovery/rest?version=v4',
          'https://www.googleapis.com/discovery/v1/apis/drive/v3/rest',
          'https://www.googleapis.com/discovery/v1/apis/youtube/v3/rest'
        ]
      });

      // Initialize Google Identity Services
      if (!window.google?.accounts?.oauth2) {
        throw new Error('Google Identity Services not loaded');
      }

      // Try to restore token first
      await this.restoreToken();

      // Initialize token client
      this.tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/drive.metadata https://www.googleapis.com/auth/youtube.readonly',
        callback: (response: any) => {
          if (response.error) {
            console.error('Token client error:', response.error);
            localStorage.removeItem('google_token');
          } else {
            this.handleTokenResponse(response);
          }
        }
      });

      this.initialized = true;
    } catch (err) {
      console.error('Failed to initialize:', err);
      throw err;
    }
  }

  private async restoreToken(): Promise<void> {
    const storedToken = localStorage.getItem('google_token');
    if (!storedToken) {
      console.log('No stored token found');
      return;
    }

    try {
      const token = JSON.parse(storedToken);
      console.log('Attempting to restore token:', token);
      
      if (!token.access_token) {
        console.log('Token missing access_token, removing from storage');
        localStorage.removeItem('google_token');
        return;
      }

      window.gapi.client.setToken(token);

      // Test the token with a simple API call
      try {
        await window.gapi.client.sheets.spreadsheets.values.get({
          spreadsheetId: '1zq7ocPrup-JLEpt5EYdIHNOFuFTO84gBO2fEACdDCxk',
          range: 'Sheet1!A1:A1'
        });
        console.log('Token restored successfully');
      } catch (error) {
        console.error('Token validation failed:', error);
        throw error;
      }
    } catch (error) {
      console.error('Failed to restore token:', error);
      localStorage.removeItem('google_token');
      window.gapi.client.setToken(null);
    }
  }

  private handleTokenResponse(response: any): void {
    console.log('Handling token response');
    window.gapi.client.setToken(response);
    localStorage.setItem('google_token', JSON.stringify(response));
    console.log('Token stored successfully');
  }

  async signIn(): Promise<void> {
    console.log('Starting sign in process...');
    return new Promise((resolve, reject) => {
      if (!this.tokenClient) {
        console.error('Token client not initialized');
        reject(new Error('Not initialized'));
        return;
      }

      this.tokenClient.callback = (response: any) => {
        console.log('Sign in callback received:', response);
        if (response.error) {
          console.error('Sign in error:', response.error);
          localStorage.removeItem('google_token');
          reject(response.error);
        } else {
          console.log('Setting token in gapi.client');
          window.gapi.client.setToken(response);
          console.log('Token set, storing in localStorage');
          localStorage.setItem('google_token', JSON.stringify(response));
          console.log('Token stored in localStorage');
          resolve();
        }
      };

      console.log('Requesting access token...');
      try {
        this.tokenClient.requestAccessToken();
      } catch (error) {
        console.error('Error requesting access token:', error);
        localStorage.removeItem('google_token');
        reject(error);
      }
    });
  }

  isSignedIn(): boolean {
    return !!window.gapi.client.getToken();
  }

  async listFiles(folderId?: string, orderBy?: string, pageSize?: number): Promise<DriveFile[]> {
    try {
      if (!this.initialized) {
        await this.init();
      }
      if (!this.isSignedIn()) {
        await this.signIn();
      }

      const queryParts = [`mimeType='application/vnd.google-apps.spreadsheet'`, 'trashed = false'];
      if (folderId) {
        queryParts.push(`'${folderId}' in parents`);
      }
      const query = queryParts.join(' and ');
      console.log(`[Drive] Listing files with query: ${query}, orderBy: ${orderBy}, pageSize: ${pageSize}`);

      // Construct request parameters dynamically
      const requestParams: any = {
        q: query,
        fields: 'files(id, name, modifiedTime)', // Ensure modifiedTime is fetched for sorting
      };
      if (orderBy) {
        requestParams.orderBy = orderBy;
      }
      if (pageSize) {
        requestParams.pageSize = pageSize;
      }

      const response = await (window.gapi.client.drive.files as any).list(requestParams);

      console.log('[Drive] API list response:', response); // Log the full response

      if (!response?.result?.files) {
        console.warn('[Drive] No files found or invalid response structure.');
        return [];
      }

      console.log(`[Drive] Found files:`, response.result.files);
      return response.result.files as DriveFile[];

    } catch (error) {
      console.error('[Drive] Error listing files:', error);
      throw error;
    }
  }

  // Add rate limiting helper
  private async throttleRequest<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    // Check if we're exceeding the rate limit
    const now = Date.now();
    this.requestTimestamps = this.requestTimestamps.filter(time => now - time < this.REQUEST_WINDOW_MS);
    
    if (this.requestTimestamps.length >= this.MAX_REQUESTS_PER_MINUTE) {
      // Calculate wait time to stay within rate limits
      const oldestTimestamp = this.requestTimestamps[0];
      const waitTime = this.REQUEST_WINDOW_MS - (now - oldestTimestamp) + 500; // Add 500ms buffer
      
      console.log(`[GoogleSheetsService] Rate limit reached (${this.requestTimestamps.length}/${this.MAX_REQUESTS_PER_MINUTE} requests in last minute). Waiting ${waitTime}ms before next request.`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    } else if (this.requestTimestamps.length > 0) {
      // Add minimum delay between requests to avoid bursty behavior
      const lastRequestTime = this.requestTimestamps[this.requestTimestamps.length - 1];
      const timeSinceLastRequest = now - lastRequestTime;
      
      if (timeSinceLastRequest < this.MIN_REQUEST_DELAY_MS) {
        const delayNeeded = this.MIN_REQUEST_DELAY_MS - timeSinceLastRequest;
        console.log(`[GoogleSheetsService] Adding minimum delay of ${delayNeeded}ms between requests`);
        await new Promise(resolve => setTimeout(resolve, delayNeeded));
      }
    }
    
    // Add current request to timestamps
    this.requestTimestamps.push(Date.now());
    console.log(`[GoogleSheetsService] Making request (${this.requestTimestamps.length}/${this.MAX_REQUESTS_PER_MINUTE} in last minute)`);
    
    // Use shared promise for identical requests
    if (!this.requestQueue[key]) {
      this.requestQueue[key] = requestFn().finally(() => {
        delete this.requestQueue[key];
      });
    } else {
      console.log(`[GoogleSheetsService] Reusing in-flight request for key: ${key}`);
    }
    
    return this.requestQueue[key];
  }

  // Add helper method to format ranges correctly
  private formatRangeWithQuotes(range: string): string {
    // If range contains no '!' character, it's not a sheet name + range format
    if (!range.includes('!')) {
      return range;
    }

    // Split the range into sheet name and cell range
    const [sheetName, cellRange] = range.split('!');
    
    // Check if sheet name contains special characters that require quotes
    // Special characters include: spaces, hyphens, periods, etc.
    const requiresQuotes = /[^A-Za-z0-9]/.test(sheetName) && 
                           !sheetName.startsWith("'") && 
                           !sheetName.endsWith("'");
    
    // If sheet name requires quotes, add them, otherwise return original range
    return requiresQuotes ? `'${sheetName}'!${cellRange}` : range;
  }

  async getData(spreadsheetId: string, range: string): Promise<SheetData> {
    // Format the range to handle special characters in sheet names
    const formattedRange = this.formatRangeWithQuotes(range);
    const cacheKey = `${spreadsheetId}:${formattedRange}`;
    
    return this.throttleRequest(cacheKey, async () => {
    try {
      if (!this.initialized) {
        await this.init();
      }

      if (!this.isSignedIn()) {
        await this.signIn();
      }

      console.log('[GoogleSheetsService] Fetching data from spreadsheet:', { spreadsheetId, range: formattedRange });
      
      const response = await window.gapi.client.sheets.spreadsheets.values.get({
        spreadsheetId,
        range: formattedRange
      });

      if (!response.result) {
        throw new Error('No response received from Google Sheets API');
      }

      console.log('[GoogleSheetsService] Received data from range:', formattedRange);
      return response.result;
    } catch (error) {
      console.error('[GoogleSheetsService] Detailed error getting data:', error);
      
      // Check if it's a Google API error
      if (error && typeof error === 'object' && 'result' in error) {
        const apiError = error as { result: { error: { message: string; status: string; } } };
        if (apiError.result?.error?.message) {
          const errorMessage = apiError.result.error.message;
          const status = apiError.result.error.status;
          
          // Provide more user-friendly error messages
          if (status === '404') {
            throw new Error('Spreadsheet not found. Please check if it still exists.');
          } else if (status === '403') {
            throw new Error('Access denied. Please check your permissions for this spreadsheet.');
            } else if (status === '429') {
              // Rate limiting error - try again with exponential backoff
            console.log('[GoogleSheetsService] Hit rate limit, retrying after longer delay...');
            await new Promise(resolve => setTimeout(resolve, 5000)); // Increased delay to 5 seconds
              return this.getData(spreadsheetId, range);
          } else {
            throw new Error(`Google Sheets error: ${errorMessage}`);
          }
        }
      }
      
      // If it's a standard Error object
      if (error instanceof Error) {
        throw error;
      }
      
      // For any other type of error
      throw new Error(`Failed to load spreadsheet data: ${String(error)}`);
    }
    });
  }

  async updateData(spreadsheetId: string, range: string, values: any[][]): Promise<void> {
    // Format the range to handle special characters in sheet names
    const formattedRange = this.formatRangeWithQuotes(range);
    
    try {
      if (!this.initialized) {
        await this.init();
      }

      if (!this.isSignedIn()) {
        await this.signIn();
      }

      console.log('Attempting to update sheet with:', { spreadsheetId, range: formattedRange, values });

      const response = await window.gapi.client.sheets.spreadsheets.values.update({
        spreadsheetId,
        range: formattedRange,
        valueInputOption: 'USER_ENTERED',
        resource: { values }
      });

      if (!response.result) {
        console.error('No response result from Google Sheets API');
        throw new Error('No response from Google Sheets API');
      }

      // Log success details
      console.log('Cell update successful:', {
        spreadsheetId,
        range: formattedRange,
        updatedRange: response.result.updatedRange,
        updatedRows: response.result.updatedRows,
        updatedColumns: response.result.updatedColumns,
        updatedCells: response.result.updatedCells
      });

    } catch (error) {
      console.error('Detailed error updating data:', error);
      
      // Check if it's a Google API error
      if (error && typeof error === 'object' && 'result' in error) {
        const apiError = error as { result: { error: { message: string } } };
        if (apiError.result?.error?.message) {
          throw new Error(apiError.result.error.message);
        }
      }
      
      // If it's a standard Error object
      if (error instanceof Error) {
        throw error; // Preserve the original error
      }
      
      // For any other type of error
      throw new Error(`Update failed: ${String(error)}`);
    }
  }

  // Add method to verify Google API client access
  async verifyApiAccess(): Promise<boolean> {
    console.log('[GoogleSheetsService] Verifying API access and permissions');
    try {
      if (!this.initialized) {
        console.log('[GoogleSheetsService] API not initialized, initializing now');
        await this.init();
      }

      if (!this.isSignedIn()) {
        console.log('[GoogleSheetsService] Not signed in, attempting sign in');
        await this.signIn();
      }
      
      // Check if we have the sheets API loaded
      if (!window.gapi.client.sheets) {
        console.error('[GoogleSheetsService] Google Sheets API client not loaded');
        return false;
      }
      
      // Check token status
      const token = window.gapi.client.getToken();
      if (!token || !token.access_token) {
        console.error('[GoogleSheetsService] No valid access token available');
        return false;
      }
      
      // Check if we have the required scopes
      // This doesn't directly verify scopes but will test a basic API call
      try {
        // Try a simple test API call - list spreadsheets (requires drive.readonly scope)
        console.log('[GoogleSheetsService] Testing API access with a list files call');
        await (window.gapi.client.drive as any).files.list({
          pageSize: 1,
          fields: 'files(id, name)',
          q: "mimeType='application/vnd.google-apps.spreadsheet'"
        });
        
        console.log('[GoogleSheetsService] Successfully verified list files API access');
        
        // Now test write access with a dummy spreadsheet update
        // First create a test spreadsheet to verify write permissions
        console.log('[GoogleSheetsService] Testing write access');
        const testId = '1zq7ocPrup-JLEpt5EYdIHNOFuFTO84gBO2fEACdDCxk'; // Billboard test sheet
        const testRange = 'Sheet1!Z1';
        
        try {
          await (window.gapi.client.sheets.spreadsheets.values as any).get({
            spreadsheetId: testId,
            range: testRange
          });
          console.log('[GoogleSheetsService] Successfully verified read access to test spreadsheet');
        } catch (readError) {
          console.error('[GoogleSheetsService] Cannot read test spreadsheet:', readError);
          console.log('[GoogleSheetsService] Will continue and try write permission anyway');
        }
        
        // Send a no-op update to verify permissions
        console.log('[GoogleSheetsService] Attempting no-op update to verify write permissions');
        try {
          // Check if the test sheet exists first
          await (window.gapi.client.sheets.spreadsheets as any).get({
            spreadsheetId: testId
          });
          
          // Now attempt a dummy update
          await (window.gapi.client.sheets.spreadsheets.values as any).update({
            spreadsheetId: testId,
            range: testRange,
            valueInputOption: 'USER_ENTERED',
            resource: {
              values: [['ApiCheck']]
            }
          });
          console.log('[GoogleSheetsService] Successfully verified write access');
          
          // Restore the cell value
          await (window.gapi.client.sheets.spreadsheets.values as any).update({
            spreadsheetId: testId,
            range: testRange,
            valueInputOption: 'USER_ENTERED',
            resource: {
              values: [['']]
            }
          });
        } catch (writeError) {
          console.error('[GoogleSheetsService] Cannot write to test spreadsheet, may lack write permissions:', writeError);
          return false;
        }
        
        return true;
      } catch (apiError) {
        console.error('[GoogleSheetsService] Failed API verification call:', apiError);
        return false;
      }
    } catch (error) {
      console.error('[GoogleSheetsService] Error verifying API access:', error);
      return false;
    }
  }

  async batchUpdateValues(spreadsheetId: string, updates: { range: string, values: any[][] }[]): Promise<void> {
    try {
      if (!this.initialized) {
        await this.init();
      }

      if (!this.isSignedIn()) {
        await this.signIn();
      }

      // Verify API access and permissions
      console.log('[GoogleSheetsService] Verifying API access before batch update');
      const hasAccess = await this.verifyApiAccess();
      if (!hasAccess) {
        console.error('[GoogleSheetsService] API access verification failed, may not have write permissions');
        // Continue anyway since we've already attempted to fix issues in verifyApiAccess
      } else {
        console.log('[GoogleSheetsService] API access verified successfully');
      }

      // Format each range in the updates
      const formattedUpdates = updates.map(update => ({
        range: this.formatRangeWithQuotes(update.range),
        values: update.values
      }));

      console.log('[GoogleSheetsService] Attempting to batch update sheet with:', { 
        spreadsheetId, 
        updates: JSON.stringify(formattedUpdates)
      });

      // First, check if the spreadsheet exists and is accessible
      try {
        const spreadsheetInfo = await this.getSpreadsheet(spreadsheetId);
        console.log(`[GoogleSheetsService] Confirmed spreadsheet access for ID: ${spreadsheetId}, Title: ${spreadsheetInfo?.properties?.title || 'Unknown'}`);
      } catch (accessError) {
        console.error(`[GoogleSheetsService] Failed to access spreadsheet ${spreadsheetId}:`, accessError);
        throw new Error(`Cannot access spreadsheet ${spreadsheetId}: ${accessError instanceof Error ? accessError.message : String(accessError)}`);
      }
      
      // Throttle the request to avoid rate limits
      await this.throttleRequest(`batchUpdate-${spreadsheetId}`, async () => {
        console.log(`[GoogleSheetsService] Executing throttled batch update for ${spreadsheetId} with ${formattedUpdates.length} updates`);
      });

      const data = {
        valueInputOption: 'USER_ENTERED',
        data: formattedUpdates
      };

      try {
        // Log the exact spreadsheet ID and update ranges/values for clear debugging
        console.log(`[GoogleSheetsService] SHEET ID: "${spreadsheetId}"`);
        formattedUpdates.forEach((update, i) => {
          console.log(`[GoogleSheetsService] UPDATE ${i+1} - Range: "${update.range}", Values: ${JSON.stringify(update.values)}`);
        });
        
        // Verify auth token before making the request
        const token = window.gapi.client.getToken();
        if (!token) {
          console.error('[GoogleSheetsService] No auth token found. Authentication may have expired.');
          await this.signIn();
          console.log('[GoogleSheetsService] Re-authenticated with Google API');
        } else {
          console.log('[GoogleSheetsService] Auth token exists, proceeding with API call');
          // Mask token for logging
          const maskedToken = token.access_token ? 
            `${token.access_token.substring(0, 5)}...${token.access_token.substring(token.access_token.length - 5)}` : 
            'null';
          console.log(`[GoogleSheetsService] Using auth token: ${maskedToken}`);
        }

        // Check if we've initialized the API client properly
        if (!window.gapi.client.sheets) {
          console.error('[GoogleSheetsService] Google Sheets API client not initialized');
          throw new Error('Google Sheets API client not initialized');
        }
        
        console.log('[GoogleSheetsService] Sending batchUpdate request to Google Sheets API');
        
        // Make the actual API call
      const response = await (window.gapi.client.sheets.spreadsheets.values as any).batchUpdate({
        spreadsheetId,
        resource: data
      });

        if (!response) {
          console.error('[GoogleSheetsService] No response received from API call');
          throw new Error('No response received from Google Sheets API');
        }

      if (!response.result) {
          console.error('[GoogleSheetsService] Response missing result object:', response);
          throw new Error('No response result from Google Sheets API');
      }

        console.log('[GoogleSheetsService] Batch update successful. Response:', JSON.stringify(response.result));

        // Return details about the update for verification
        if (response.result.totalUpdatedCells) {
          console.log(`[GoogleSheetsService] Total cells updated: ${response.result.totalUpdatedCells}`);
        }
        
        if (response.result.totalUpdatedSheets) {
          console.log(`[GoogleSheetsService] Total sheets updated: ${response.result.totalUpdatedSheets}`);
        }
        
        if (response.result.responses) {
          console.log(`[GoogleSheetsService] Update responses: ${JSON.stringify(response.result.responses)}`);
        }

        return response.result;
      } catch (apiError) {
        console.error('[GoogleSheetsService] API error in batch update. Full error:', apiError);
      
        // Try to get more detailed error information
        let errorMessage = 'Unknown API error';
        
        if (apiError && typeof apiError === 'object') {
          if ('result' in apiError) {
            const typedError = apiError as { result?: { error?: { message?: string, errors?: any[], code?: number, status?: string } } };
            if (typedError.result?.error) {
              const error = typedError.result.error;
              errorMessage = error.message || 'API error without message';
              console.error('[GoogleSheetsService] API error details:', { 
                message: error.message,
                code: error.code,
                status: error.status,
                errors: error.errors ? JSON.stringify(error.errors) : 'none'
              });
            }
          } else if ('error' in apiError) {
            const typedError = apiError as { error?: { message?: string, errors?: any[], code?: number, status?: string } };
            if (typedError.error) {
              errorMessage = typedError.error.message || 'API error without message';
              console.error('[GoogleSheetsService] Root error details:', { 
                message: typedError.error.message,
                code: typedError.error.code,
                status: typedError.error.status,
                errors: typedError.error.errors ? JSON.stringify(typedError.error.errors) : 'none'
              });
            }
          } else if ('message' in apiError) {
            errorMessage = (apiError as Error).message;
          } else {
            console.error('[GoogleSheetsService] Unstructured API error:', apiError);
            errorMessage = JSON.stringify(apiError);
          }
        }
        
        throw new Error(`Sheets API batch update error: ${errorMessage}`);
      }
    } catch (error) {
      console.error('[GoogleSheetsService] Detailed error performing batch update:', error);
      
      // If it's a standard Error object
      if (error instanceof Error) {
        throw error; // Preserve the original error
      }
      
      // For any other type of error
      throw new Error(`Batch update failed: ${String(error)}`);
    }
  }

  async getSheets(spreadsheetId: string): Promise<string[]> {
    try {
      // Use type assertion to bypass TypeScript's type checking
      const response = await (window.gapi.client.sheets.spreadsheets as any).get({
        spreadsheetId
      });
      
      if (!response.result.sheets || response.result.sheets.length === 0) {
        throw new Error('No sheets found in the spreadsheet');
      }
      
      return response.result.sheets.map((sheet: any) => sheet.properties.title);
    } catch (error) {
      console.error('Error getting sheets:', error);
      throw error;
    }
  }

  async create(name: string, folderId: string): Promise<{ id: string }> {
    try {
      if (!this.initialized) {
        await this.init();
      }

      if (!this.isSignedIn()) {
        await this.signIn();
      }

      // Cast the drive API to any to bypass TypeScript's type checking
      const driveApi = window.gapi.client.drive as any;
      const response = await driveApi.files.create({
        resource: {
          name,
          mimeType: 'application/vnd.google-apps.spreadsheet',
          parents: [folderId]
        }
      });

      if (!response.result || !response.result.id) {
        throw new Error('Failed to create spreadsheet');
      }

      return { id: response.result.id };
    } catch (error) {
      console.error('Error creating spreadsheet:', error);
      throw error;
    }
  }

  async deleteRow(spreadsheetId: string, range: string): Promise<void> {
    // Format the range to handle special characters in sheet names
    const formattedRange = this.formatRangeWithQuotes(range);
    
    if (!formattedRange.includes('!')) {
      throw new Error('Invalid range format. Expected format: Sheet1!A1:B2');
    }

    try {
      if (!this.initialized) {
        await this.init();
      }

      if (!this.isSignedIn()) {
        await this.signIn();
      }

      // Extract sheet name and range
      const [sheetName, cellRange] = formattedRange.split('!');
      
      // Remove quotes if present in sheetName
      const cleanSheetName = sheetName.replace(/^'|'$/g, '');

      // Extract row number from cell reference (e.g., A2 -> 2)
      const rowMatch = cellRange.match(/\D+(\d+)/);
      if (!rowMatch || !rowMatch[1]) {
        throw new Error(`Could not extract row number from range: ${cellRange}`);
      }

      const rowIndex = parseInt(rowMatch[1], 10) - 1; // Convert to 0-based index

      // Get the sheet ID
      const spreadsheet = await this.getSpreadsheet(spreadsheetId);
      const sheet = spreadsheet.sheets.find((s: any) => s.properties.title === cleanSheetName);
      
      if (!sheet) {
        throw new Error(`Sheet "${cleanSheetName}" not found in spreadsheet`);
      }

      const sheetId = sheet.properties.sheetId;

      // Delete the row
      const request = {
        spreadsheetId,
        resource: {
          requests: [
            {
            deleteDimension: {
              range: {
                  sheetId,
                dimension: 'ROWS',
                  startIndex: rowIndex,
                  endIndex: rowIndex + 1
              }
            }
        }
          ]
        }
      };

      await (window.gapi.client.sheets.spreadsheets as any).batchUpdate(request);
      console.log(`Successfully deleted row at ${formattedRange}`);
    } catch (error) {
      console.error('Error deleting row:', error);
      throw error;
    }
  }

  async getVideoInfo(videoId: string): Promise<{ song: string; artist: string } | null> {
    try {
      const videoUrl = `https://www.youtube.com/watch?v=${videoId}`;
      const songInfo = await youtubeService.getSongInfo(videoUrl);
      return {
        song: songInfo.song,
        artist: songInfo.artist
      };
    } catch (error) {
      console.error('Error fetching video info:', error);
      return null;
    }
  }

  async signOut(): Promise<void> {
    try {
      const token = window.gapi.client.getToken();
      if (token) {
        window.google.accounts.oauth2.revoke(token.access_token);
        window.gapi.client.setToken(null);
        localStorage.removeItem('google_token');
      }
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  // Add this new method to batch get multiple cell values
  async batchGetValues(spreadsheetId: string, ranges: string[]): Promise<{[range: string]: any}> {
    // Format all ranges to handle special characters in sheet names
    const formattedRanges = ranges.map(range => this.formatRangeWithQuotes(range));
    
      try {
        if (!this.initialized) {
          await this.init();
        }

        if (!this.isSignedIn()) {
          await this.signIn();
        }

      // Create request parameters
      const params = {
          spreadsheetId,
        ranges: formattedRanges,
        valueRenderOption: 'UNFORMATTED_VALUE',
        dateTimeRenderOption: 'FORMATTED_STRING'
      };

      console.log(`[GoogleSheetsService] Fetching batch data with ranges:`, formattedRanges);

      // Make the request
      const response = await (window.gapi.client.sheets.spreadsheets.values as any).batchGet(params);

      // Process the response
      const result: {[range: string]: any} = {};
      if (response.result && response.result.valueRanges) {
        response.result.valueRanges.forEach((valueRange: any, index: number) => {
          // Use the original range as the key for consistency with caller's expectations
          result[ranges[index]] = valueRange;
        });
      }

      return result;
    } catch (error) {
      console.error('[GoogleSheetsService] Error in batchGetValues:', error);
      throw error;
    }
  }

  async getSpreadsheet(spreadsheetId: string): Promise<any> {
    return this.throttleRequest(`spreadsheet:${spreadsheetId}`, async () => {
      try {
        if (!this.initialized) {
          await this.init();
        }

        if (!this.isSignedIn()) {
          await this.signIn();
        }

        console.log('[GoogleSheetsService] Fetching spreadsheet metadata:', spreadsheetId);
        
        // Use type assertion to bypass potentially incorrect type definitions
        const response = await (window.gapi.client.sheets.spreadsheets as any).get({
          spreadsheetId
        });

        if (!response.result) {
          throw new Error('No response received from Google Sheets API');
        }

        return response.result;
      } catch (error) {
        console.error('[GoogleSheetsService] Error getting spreadsheet:', error);
        throw error;
            }
          });
        }

  async sortRange(
    spreadsheetId: string, 
    range: string,
    sortSpecs?: { dimensionIndex: number; sortOrder: 'ASCENDING' | 'DESCENDING'; }[]
  ): Promise<void> {
    // Format the range to handle special characters in sheet names
    const formattedRange = this.formatRangeWithQuotes(range);
    
    if (!formattedRange.includes('!')) {
      throw new Error('Invalid range format. Expected format: Sheet1!A2:C');
    }

    try {
      if (!this.initialized) {
        await this.init();
      }

      if (!this.isSignedIn()) {
        await this.signIn();
      }

      // Extract sheet name and range
      const [sheetName, cellRange] = formattedRange.split('!');
      
      // Remove quotes if present in sheetName
      const cleanSheetName = sheetName.replace(/^'|'$/g, '');

      // Get sheet ID
      const spreadsheet = await this.getSpreadsheet(spreadsheetId);
      const sheet = spreadsheet.sheets.find((s: any) => s.properties.title === cleanSheetName);
      
      if (!sheet) {
        throw new Error(`Sheet "${cleanSheetName}" not found in spreadsheet`);
      }

      const sheetId = sheet.properties.sheetId;

      // Parse the cell range (e.g., A2:C)
      // Assuming the range is something like A2:C or A2:C10
      const rangeMatch = cellRange.match(/([A-Z]+)(\d+):([A-Z]+)(\d*)/);
      if (!rangeMatch) {
        throw new Error(`Invalid cell range format: ${cellRange}`);
      }

      // Extract column letters and indices
      const startCol = rangeMatch[1];
      const startRow = parseInt(rangeMatch[2], 10) - 1; // Convert to 0-indexed
      
      // Calculate column index (0-indexed)
      // Convert column letter to index (A=0, B=1, etc.)
      const startColIndex = this.columnLetterToIndex(startCol);
      
      // Create the sort request
      const request: any = {
        spreadsheetId,
        resource: {
          requests: [
            {
              sortRange: {
                range: {
                  sheetId,
                  startRowIndex: startRow,
                  startColumnIndex: startColIndex,
                  // If end indices are not specified, the sort will go to the end of the data
                },
                // Conditionally add sortSpecs if provided
              }
            }
          ]
        }
      };

      // Add sortSpecs if they are provided
      if (sortSpecs && sortSpecs.length > 0) {
        request.resource.requests[0].sortRange.sortSpecs = sortSpecs;
      }

      await (window.gapi.client.sheets.spreadsheets as any).batchUpdate(request);
      console.log(`Successfully sorted range ${formattedRange}` + (sortSpecs ? ` with specs: ${JSON.stringify(sortSpecs)}` : ''));
      } catch (error) {
      console.error('Error sorting range:', error);
      throw error;
    }
  }

  // Helper method to convert column letter to index
  private columnLetterToIndex(column: string): number {
    let index = 0;
    for (let i = 0; i < column.length; i++) {
      index = index * 26 + column.charCodeAt(i) - 'A'.charCodeAt(0) + 1;
    }
    return index - 1; // Convert to 0-indexed
  }

  // ---> START: Add Drive copyFile method
  async copyFile(fileId: string, destinationFolderId?: string, copyPermissions: boolean = true): Promise<DriveCopyResponse> {
    console.log(`[Drive] Copying file ID: ${fileId} to folder ID: ${destinationFolderId}, copyPermissions: ${copyPermissions}`);
    try {
      if (!this.initialized) await this.init();
      if (!this.isSignedIn()) await this.signIn();

      const accessToken = window.gapi.client.getToken()?.access_token;
      if (!accessToken) {
        console.error("[Drive] Access token not available for copyFile operation.");
        throw new Error('Access token not available for copyFile operation');
      }

      const requestBody: any = {};
      if (destinationFolderId) {
        requestBody.parents = [destinationFolderId];
      }

      // Use gapi.client.request to explicitly use OAuth token
      const response = await (window.gapi.client as any).request({
        path: `https://www.googleapis.com/drive/v3/files/${fileId}/copy`,
        method: 'POST',
        params: { fields: 'id, name, mimeType' }, // Standard fields for copy response
        headers: {
          'Authorization': `Bearer ${accessToken}`
        },
        body: requestBody
      });
      
      console.log('[Drive] Copy response (using gapi.client.request):', response);
      
      const copiedFileResult = response.result as DriveCopyResponse;

      if (copyPermissions && copiedFileResult && copiedFileResult.id) {
        try {
          const newFileId = copiedFileResult.id;
          const permissions = await this.getFilePermissions(fileId);
          console.log(`[Drive] Found ${permissions.length} permissions to copy`);
          
          for (const permission of permissions) {
            if (permission.role === 'owner') {
              console.log('[Drive] Skipping owner permission as it cannot be transferred');
              continue;
            }
            // Ensure the permission object being passed to addFilePermission has the expected structure.
            // The DrivePermission type definition might be relevant here.
            // For now, assuming 'permission' object is directly usable.
            await this.addFilePermission(newFileId, permission);
          }
          console.log('[Drive] Successfully copied permissions');
        } catch (permError) {
          console.error('[Drive] Error copying permissions:', permError);
        }
      }
      
      return copiedFileResult;
    } catch (error) {
      console.error(`[Drive] Error copying file ${fileId}:`, error);
      // Check if error is due to token issue, potentially trigger re-auth or specific logging
      if ((error as any)?.result?.error?.code === 401 || (error as any)?.result?.error?.code === 403) {
         console.error('[Drive] Copy failed with auth error, consider token refresh or scope check.');
      }
      throw error;
    }
  }
  // ---> END: Add Drive copyFile method

  // ---> START: Add Drive renameFile method
  async renameFile(fileId: string, newName: string): Promise<DriveCopyResponse> {
    console.log(`[Drive] Renaming file ID: ${fileId} to "${newName}"`);
    try {
      if (!this.initialized) await this.init();
      if (!this.isSignedIn()) await this.signIn();

      const requestBody: DriveRenameRequest = { name: newName };

      // Use standard Promise return, casting to any to bypass potential type issues
      const driveFilesApi = window.gapi.client.drive.files as any;
      const response = await driveFilesApi.update({
        fileId: fileId,
        fields: 'id, name, mimeType',
        resource: requestBody
      });

      console.log('[Drive] Rename response:', response);
      return response.result as DriveCopyResponse;
    } catch (error) {
      console.error(`[Drive] Error renaming file ${fileId}:`, error);
      throw error;
    }
  }
  // ---> END: Add Drive renameFile method

  // ---> START: Add duplicateAndRenameSheet method
  async duplicateAndRenameSheet(folderId: string, newName: string): Promise<{ id: string }> {
    console.log(`[Drive] Finding most recent sheet in folder ${folderId} to duplicate and rename to "${newName}"`);
    try {
      if (!this.initialized) await this.init();
      if (!this.isSignedIn()) await this.signIn();

      // 1. List files in the folder
      const files = await this.listFiles(folderId);
      
      // 2. Filter out any template files and non-date files
      const sheetFiles = files.filter(file => {
        // Skip template and render sheets
        if (file.name.includes('Template') || file.name.includes('-render')) {
          return false;
        }
        
        // Check if the filename matches our date format (M-d-yy)
        const dateRegex = /^\d{1,2}-\d{1,2}-\d{2}$/;
        return dateRegex.test(file.name);
      });
      
      console.log(`[Drive] Found ${sheetFiles.length} date-formatted sheets in the folder`);
      
      if (sheetFiles.length === 0) {
        throw new Error('No existing date sheets found to duplicate');
      }
      
      // 3. Sort files by date (newest first)
      const sortedFiles = sheetFiles.sort((a, b) => {
        // Parse dates from filenames (M-d-yy format)
        const dateA = this.parseDateFromFilename(a.name);
        const dateB = this.parseDateFromFilename(b.name);
        
        // Sort descending (newest first)
        return dateB.getTime() - dateA.getTime();
      });
      
      // 4. Get the most recent file
      const mostRecentFile = sortedFiles[0];
      console.log(`[Drive] Most recent sheet: ${mostRecentFile.name} (ID: ${mostRecentFile.id})`);
      
      // 5. Copy the file
      const copiedFile = await this.copyFile(mostRecentFile.id, folderId, true);
      console.log(`[Drive] Copied file with new ID: ${copiedFile.id}`);
      
      // 6. Rename the file
      await this.renameFile(copiedFile.id, newName);
      console.log(`[Drive] Renamed copied file to: ${newName}`);
      
      return { id: copiedFile.id };
    } catch (error) {
      console.error(`[Drive] Error duplicating and renaming sheet:`, error);
      throw error;
    }
  }
  
  // Helper method to parse date from filename (M-d-yy format)
  private parseDateFromFilename(filename: string): Date {
    try {
      const [month, day, yearShort] = filename.split('-').map(part => parseInt(part, 10));
      const year = 2000 + yearShort; // Convert 2-digit year to 4-digit
      return new Date(year, month - 1, day); // month is 0-indexed in JS Date
    } catch (error) {
      console.error(`[Drive] Error parsing date from filename ${filename}:`, error);
      // Return a very old date if parsing fails (to sort it last)
      return new Date(1970, 0, 1);
    }
  }
  // ---> END: Add duplicateAndRenameSheet method

  // ---> START: Add methods for handling file permissions
  async getFilePermissions(fileId: string): Promise<DrivePermission[]> {
    console.log(`[Drive] Getting permissions for file ID: ${fileId}`);
    try {
      if (!this.initialized) await this.init();
      if (!this.isSignedIn()) await this.signIn();

      // Cast the entire gapi.client to any, then access drive.permissions
      const gapiClient: any = window.gapi.client;
      const response = await gapiClient.drive.permissions.list({
        fileId: fileId,
        fields: 'permissions(id,type,emailAddress,role,domain,allowFileDiscovery)'
      });

      console.log(`[Drive] Got ${response.result.permissions?.length || 0} permissions`);
      return (response.result.permissions || []) as DrivePermission[];
    } catch (error) {
      console.error(`[Drive] Error getting permissions for file ${fileId}:`, error);
      return [];
    }
  }

  async addFilePermission(fileId: string, permission: DrivePermission): Promise<void> {
    console.log(`[Drive] Adding permission to file ID: ${fileId}`, permission);
    try {
      if (!this.initialized) await this.init();
      if (!this.isSignedIn()) await this.signIn();
      
      const permissionBody = {
        role: permission.role,
        type: permission.type,
        ...(permission.emailAddress ? { emailAddress: permission.emailAddress } : {}),
        ...(permission.domain ? { domain: permission.domain } : {})
      };
      
      // Cast the entire gapi.client object to any
      const gapiClient = window.gapi.client as any;
      await gapiClient.drive.permissions.create({
        fileId: fileId,
        resource: permissionBody,
        sendNotificationEmail: false
      });
      
      console.log(`[Drive] Successfully added permission to file ${fileId}`);
    } catch (error) {
      console.error(`[Drive] Error adding permission to file ${fileId}:`, error);
      throw error;
    }
  }

  async getFileInfo(fileId: string): Promise<DriveFile | null> {
    console.log(`[Drive] Getting file info for file ID: ${fileId}`);
    try {
      if (!this.initialized) await this.init();
      if (!this.isSignedIn()) await this.signIn();

      // Cast the drive API to any to bypass TypeScript's type checking
      const gapiClient = window.gapi.client as any;
      const response = await gapiClient.drive.files.get({
        fileId: fileId,
        fields: 'id,name,mimeType,modifiedTime'
      });

      console.log(`[Drive] Got file info:`, response.result);
      return response.result as DriveFile;
    } catch (error) {
      console.error(`[Drive] Error getting file info for ${fileId}:`, error);
      return null;
    }
  }

  async fileExists(fileId: string): Promise<boolean> {
    console.log(`[Drive] Checking if file exists with ID: ${fileId}`);
    try {
      if (!this.initialized) await this.init();
      if (!this.isSignedIn()) await this.signIn();

      // Use the drive API to check if the file exists
      const gapiClient = window.gapi.client as any;
      const _response = await gapiClient.drive.files.get({
        fileId: fileId,
        fields: 'id' // Only fetch the ID field since we just need to check existence
      });

      // If we get here without an error, the file exists
      console.log(`[Drive] File with ID ${fileId} exists`);
      return true;
    } catch (error) {
      // If we get a 404 error or any other error, the file doesn't exist or is inaccessible
      console.log(`[Drive] File with ID ${fileId} does not exist or is inaccessible:`, error);
      return false;
    }
  }
  // ---> END: Add methods for handling file permissions

  // Add a new method to fetch sheet data and send it to the backend
  async fetchAndPostSheetData(spreadsheetId: string): Promise<any> {
    return this.throttleRequest(`spreadsheet-backend:${spreadsheetId}`, async () => {
    try {
      if (!this.initialized) {
        await this.init();
      }

      if (!this.isSignedIn()) {
        await this.signIn();
      }

        console.log('[GoogleSheetsService] Fetching spreadsheet for backend:', spreadsheetId);
        
        // Get spreadsheet metadata
        const spreadsheet = await this.getSpreadsheet(spreadsheetId);
        if (!spreadsheet || !spreadsheet.sheets || spreadsheet.sheets.length === 0) {
          throw new Error('No sheets found in spreadsheet');
        }
        
        // Get data from the first sheet
        const sheetTitle = spreadsheet.sheets[0].properties.title;
        const response = await window.gapi.client.sheets.spreadsheets.values.get({
        spreadsheetId,
          range: `${sheetTitle}!A1:Z1000`
        });
        
        if (!response.result || !response.result.values || response.result.values.length === 0) {
          throw new Error('No data found in spreadsheet');
        }
        
        // Format data for the backend
        const headers = response.result.values[0];
        const rows = response.result.values.slice(1);
        const sheetData = { headers, rows };
        
        // Send data to backend
        const apiResponse = await fetch('/api/sheets/data', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ spreadsheetId, sheetData })
        });
        
        if (!apiResponse.ok) {
          throw new Error(`Backend API error: ${apiResponse.status} ${apiResponse.statusText}`);
        }
        
        return await apiResponse.json();
      } catch (error) {
        console.error('[GoogleSheetsService] Error fetching and posting sheet data:', error);
        throw error;
      }
    });
  }

  // ---> Add the new getHeaders method here <---
  async getHeaders(spreadsheetId: string, sheetName: string): Promise<string[]> {
    console.log(`[SheetsService] Getting headers for: ${spreadsheetId}, Sheet: ${sheetName}`);
    try {
      if (!this.initialized) {
        await this.init();
      }
      if (!this.isSignedIn()) {
        // Decide if sign-in should be attempted automatically here or if it should fail
        console.log('[SheetsService getHeaders] Not signed in. Attempting sign-in...');
        await this.signIn(); 
      }

      // Construct the range for the first THREE rows to ensure we don't miss headers
      // Some spreadsheets may have multi-row headers or empty first cells
      const range = `${sheetName}!1:3`; 
      console.log(`[SheetsService] Requesting range ${range} from spreadsheet ${spreadsheetId}`);
      
      // Use the existing getData method (which includes throttling)
      const response = await this.getData(spreadsheetId, range);

      if (response?.values && response.values.length > 0) {
        // Check first row for headers - this is the most common case
        const firstRow = response.values[0].map(String);
        console.log('[SheetsService getHeaders] First row candidates:', firstRow);
        
        // Validate if this looks like a header row - check for common column names
        const headerKeywords = ['position', 'song', 'artist', 'title', 'name', 'url', 'link', 'id'];
        const hasHeaderKeywords = firstRow.some(cell => 
          headerKeywords.some(keyword => 
            cell.toLowerCase().includes(keyword.toLowerCase())
          )
        );
        
        if (hasHeaderKeywords) {
          console.log('[SheetsService getHeaders] First row contains header keywords, using as headers');
          // Map headers, replacing null/undefined with empty strings
          const headers = firstRow.map(h => h || '');
          console.log('[SheetsService getHeaders] Normalized headers:', headers);
          return headers;
        } 
        
        // If first row doesn't look like headers, check second row
        if (response.values.length > 1) {
          const secondRow = response.values[1].map(String);
          console.log('[SheetsService getHeaders] First row did not look like headers. Checking second row:', secondRow);
          
          const hasSecondRowHeaderKeywords = secondRow.some(cell => 
            headerKeywords.some(keyword => 
              cell.toLowerCase().includes(keyword.toLowerCase())
            )
          );
          
          if (hasSecondRowHeaderKeywords) {
            console.log('[SheetsService getHeaders] Second row contains header keywords, using as headers');
            const headers = secondRow.map(h => h || '');
            console.log('[SheetsService getHeaders] Normalized headers from second row:', headers);
            return headers;
          }
        }
        
        // If neither first nor second row look like headers, just return the first row
        console.log('[SheetsService getHeaders] Could not confidently identify header row. Using first row as default.');
        return firstRow.map(h => h || '');
      } else {
        console.warn('[SheetsService getHeaders] No rows found in the spreadsheet or empty response.');
        return []; // Return empty array if no headers found
      }
    } catch (error: any) { // Catch specific errors if needed
      console.error(`[SheetsService] Error getting headers for ${spreadsheetId} (${sheetName}):`, error);
      
      // Check for specific error conditions
      if (error?.result?.error?.status === 'PERMISSION_DENIED' || 
          error?.message?.includes('403') || 
          error?.message?.includes('permission')) {
        console.error('[SheetsService getHeaders] Permission denied (403). Please check your access to this spreadsheet.');
      } else if (error?.result?.error?.status === 'NOT_FOUND' || 
                 error?.message?.includes('404') || 
                 error?.message?.includes('not found')) {
        console.error('[SheetsService getHeaders] Spreadsheet or sheet not found (404). Please check that the spreadsheet ID and sheet name are correct.');
      } else if (error?.message?.includes('network') || 
                 error?.message?.includes('connection')) {
        console.error('[SheetsService getHeaders] Network error. Please check your internet connection.');
      }
      
      // Return empty array on error to allow fallback in components
      return []; 
            }
  }
  // ---> End of new getHeaders method <---

  // ---> Add getFileName method <---
  async getFileName(fileId: string): Promise<string> {
    console.log(`[Drive] Getting name for file ID: ${fileId}`);
    try {
      if (!this.initialized) await this.init();
      if (!this.isSignedIn()) await this.signIn();

      const response = await (window.gapi.client.drive.files as any).get({
        fileId: fileId,
        fields: 'name' // Only fetch the name field
      });

      if (response?.result?.name) {
        console.log(`[Drive] Found name: ${response.result.name}`);
        return response.result.name;
      } else {
        throw new Error(`File name not found for ID: ${fileId}`);
      }
    } catch (error) {
      console.error(`[Drive] Error getting file name for ${fileId}:`, error);
      throw error;
    }
  }

  // ---> Add findFileByName method <---
  async findFileByName(fileName: string, folderId?: string, mimeType: string = 'application/vnd.google-apps.spreadsheet'): Promise<string | null> {
    console.log(`[Drive] Searching for file named: "${fileName}" with mimeType: ${mimeType}${folderId ? ` in folder: ${folderId}` : ''}`);
    try {
      if (!this.initialized) await this.init();
      if (!this.isSignedIn()) await this.signIn();

      // Construct the query for exact name match and mime type
      const queryParts = [`name = '${fileName.replace(/'/g, "\\'")}' and mimeType = '${mimeType}' and trashed = false`];
      
      // Add folder constraint if provided
      if (folderId) {
        queryParts.push(`'${folderId}' in parents`);
      }
      
      const query = queryParts.join(' and ');
      console.log(`[Drive] Search query: ${query}`);

      const response = await (window.gapi.client.drive.files as any).list({
        q: query,
        fields: 'files(id, name)', // Fetch ID and name
        pageSize: 1 // We only expect one match
      });

      if (response?.result?.files && response.result.files.length > 0) {
        if (response.result.files.length > 1) {
            console.warn(`[Drive] Multiple files found with name "${fileName}". Returning the first one found.`);
        }
        const fileId = response.result.files[0].id;
        console.log(`[Drive] Found file "${fileName}" with ID: ${fileId}`);
        return fileId;
      } else {
        console.log(`[Drive] No file found with name "${fileName}" and mimeType "${mimeType}".`);
        return null; // Return null if no file is found
      }
    } catch (error) {
      console.error(`[Drive] Error searching for file "${fileName}":`, error);
      throw error; // Re-throw the error to be handled by the caller
    }
  }

  // ---> Add copyRange method <---
  async copyRange(sourceSpreadsheetId: string, sourceRange: string, destinationSpreadsheetId: string, destinationRange: string): Promise<void> {
      console.log(`[SheetsService] Copying range from ${sourceSpreadsheetId} (${sourceRange}) to ${destinationSpreadsheetId} (${destinationRange})`);
      try {
          if (!this.initialized) await this.init();
          if (!this.isSignedIn()) await this.signIn();

          // 1. Get data from the source range
          const sourceData = await this.getData(sourceSpreadsheetId, sourceRange);

          // Check if data exists
          if (!sourceData || !sourceData.values || sourceData.values.length === 0) {
              console.warn(`[SheetsService] No data found in source range ${sourceRange} of ${sourceSpreadsheetId}. Nothing to copy.`);
              // Optionally clear the destination range or just return
              // For now, we'll just return without erroring
              return;
          }

          const valuesToCopy = sourceData.values;
          console.log(`[SheetsService] Read ${valuesToCopy.length} rows from source.`);

          // 2. Write data to the destination range
          // Ensure the destination range matches the dimensions of the source data if needed, or handle potential size mismatches.
          // For a simple copy, we assume updateData handles overwriting.
          await this.updateData(destinationSpreadsheetId, destinationRange, valuesToCopy);

          console.log(`[SheetsService] Successfully copied data to ${destinationSpreadsheetId} (${destinationRange})`);

      } catch (error) {
          console.error(`[SheetsService] Error copying range from ${sourceSpreadsheetId} to ${destinationSpreadsheetId}:`, error);
          // Re-throw the error to be handled by the caller
          throw new Error(`Failed to copy data: ${error instanceof Error ? error.message : String(error)}`);
      }
  }

} // End of GoogleSheetsService class

export const sheetsService = new GoogleSheetsService(); 